import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput",
    "menu",
    "optionsList",
    "option",
    "createOption",
    "createOptionLabel",
    "pillContainer",
    "pill",
    "pillTemplate",
  ]

  static values = {
    existingTags: Array,
    open: Boolean,
    paramName: String,
  }

  connect() {
    this.boundSubmitHandler = this.commitPendingBeforeSubmit.bind(this)
    this.form = this.element.closest("form")
    if (this.form) this.form.addEventListener("submit", this.boundSubmitHandler)
    this.filter()
  }

  disconnect() {
    if (this.form) this.form.removeEventListener("submit", this.boundSubmitHandler)
  }

  open(event) {
    this.filter(event)
  }

  close(event) {
    if (event) event.preventDefault()
    this.openValue = false
  }

  closeOnOutside(event) {
    if (this.element.contains(event.target)) return

    this.openValue = false
  }

  openValueChanged() {
    if (!this.hasMenuTarget) return

    this.menuTarget.classList.toggle("hidden", !this.openValue)
    this.menuTarget.setAttribute("aria-hidden", this.openValue ? "false" : "true")
    this.searchInputTarget.setAttribute("aria-expanded", this.openValue ? "true" : "false")
  }

  filter(event) {
    const query = this.searchInputTarget.value.trim().toLowerCase()
    const selectedNames = this.selectedNames().map((name) => name.toLowerCase())
    const exactExistingMatch = this.existingTagsValue.some((tag) => tag.name.toLowerCase() === query)
    const exactSelectedMatch = selectedNames.includes(query)
    let visibleOptionsCount = 0

    this.optionTargets.forEach((option) => {
      const name = option.dataset.tagName.toLowerCase()
      const matches = name.includes(query)
      const alreadySelected = selectedNames.includes(name)
      const visible = matches && !alreadySelected
      option.classList.toggle("hidden", !visible)
      if (visible) visibleOptionsCount += 1
    })

    const showCreateOption = query.length > 0 && !exactExistingMatch && !exactSelectedMatch
    this.createOptionTarget.classList.toggle("hidden", !showCreateOption)
    this.createOptionLabelTarget.textContent = this.createOptionTarget.dataset.createLabelTemplate.replace(
      "__NAME__",
      showCreateOption ? this.searchInputTarget.value.trim() : ""
    )

    const hasContent = visibleOptionsCount > 0 || showCreateOption
    const isInteraction = event?.type === "input" || event?.type === "focus"
    if (isInteraction || this.openValue) this.openValue = hasContent
  }

  commitTyped(event) {
    if (event) event.preventDefault()

    const value = this.searchInputTarget.value.trim()
    if (!value) return

    const existing = this.existingTagsValue.find((tag) => tag.name.toLowerCase() === value.toLowerCase())
    const name = existing ? existing.name : value

    if (!this.hasSelectedName(name)) {
      this.addPill(name, existing ? existing.color : null)
    }

    this.searchInputTarget.value = ""
    this.filter()
    this.close()
  }

  selectExisting(event) {
    const button = event.currentTarget
    const name = button.dataset.tagName
    if (this.hasSelectedName(name)) return

    this.addPill(name, button.dataset.tagColor)
    this.searchInputTarget.value = ""
    this.filter()
    this.searchInputTarget.focus()
    this.close()
  }

  removePill(event) {
    event.currentTarget.closest("[data-tag-input-target='pill']").remove()
    this.filter()
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      this.commitTyped(event)
    } else if (event.key === "Backspace") {
      this.popLastIfEmpty(event)
    } else if (event.key === "Escape") {
      this.close(event)
    } else if (event.key === ",") {
      this.commitTyped(event)
    }
  }

  popLastIfEmpty(event) {
    if (this.searchInputTarget.value !== "") return

    const last = this.pillTargets[this.pillTargets.length - 1]
    if (!last) return

    event.preventDefault()
    last.remove()
    this.filter()
  }

  commitPendingBeforeSubmit() {
    if (this.searchInputTarget.value.trim() !== "") this.commitTyped()
  }

  selectedNames() {
    return this.pillTargets.map((pill) => pill.dataset.pillName)
  }

  hasSelectedName(name) {
    return this.selectedNames().some((selectedName) => selectedName.toLowerCase() === name.toLowerCase())
  }

  addPill(name, color) {
    const pill = this.pillTemplateTarget.content.firstElementChild.cloneNode(true)
    const hiddenInput = pill.querySelector("input[type='hidden']")
    const label = pill.querySelector("[data-tag-input-target='pillLabel']")

    pill.dataset.pillName = name
    hiddenInput.name = this.paramNameValue
    hiddenInput.value = name
    label.textContent = name

    if (color) {
      pill.classList.remove("badge-ghost")
      pill.style.backgroundColor = color
      pill.style.color = "#fff"
    } else {
      pill.classList.add("badge-ghost")
      pill.removeAttribute("style")
    }

    this.pillContainerTarget.appendChild(pill)
  }
}
